import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/levrana";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const levrana: CaseStudy = {
  slug: "levrana",
  title: "Levrana",
  cardTitle: "Разработка e-commerce платформы для производителя косметики Levrana",
  subtitle: "Мультибрендовый интернет-магазин косметики",
  industry: { label: "Красота и косметология", slug: "beauty" },
  eyebrow: "Интернет-магазин · Производитель косметики",
  h1: "Разработка e-commerce платформы для Levrana",
  lead:
    "Разработали e-commerce платформу для российского бренда Levrana: мультибрендовый каталог косметики, товаров для дома, БАДов и pet-продукции, бонусную программу и профиль покупателя — и развиваем проект после запуска.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить e-commerce платформу" },

  seo: {
    primaryKeyword: "разработка e-commerce платформы для производителя косметики",
    secondaryKeywords: [
      "интернет-магазин для производителя косметики",
      "мультибрендовый интернет-магазин косметики",
      "сайт крупного косметического бренда",
      "поддержка крупного интернет-магазина",
    ],
    semanticEntities: [
      "мультибрендовая платформа",
      "каталог косметики",
      "товары для дома",
      "БАДы",
      "pet-продукция",
      "бонусный счёт",
      "избранное",
      "корзина",
      "профиль покупателя",
    ],
    excludedKeywords: [
      "натуральная косметика купить",
      "Levrana купить",
      "крем для лица",
      "БАДы купить",
    ],
    title: "E-commerce платформа Levrana — кейс HAY.agency",
    description:
      "Кейс HAY.agency: мультибрендовый интернет-магазин Levrana с большим каталогом косметики, товаров для дома, БАДов и pet-продукции, который HAY развивает после запуска.",
    h1: "Разработка e-commerce платформы для Levrana",
    slug: "levrana",
    canonicalUrl: `${SITE_URL}/cases/levrana`,
    ogTitle: "E-commerce платформа для производителя косметики Levrana",
    ogDescription:
      "Мультибрендовый каталог, бонусная программа, корзина и профиль покупателя для российского косметического бренда.",
    ogImage: `${SITE_URL}${dir}/levrana-og.webp`,
    robotsIndex: true,
    datePublished: "2023-06-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и сценарии покупки",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога и категорий",
    },
    relatedCases: [
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "Мультибрендовый каталог косметики",
      },
      {
        label: "Разработка интернет-магазина цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и корзина",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужна e-commerce платформа для крупного бренда?",
    text: "Спроектируем мультибрендовый каталог, лояльность и корзину, соберём платформу и возьмём её на развитие.",
    button: "Обсудить e-commerce платформу",
  },

  client: {
    name: "Levrana",
    url: "https://levrana.ru/",
    city: "Санкт-Петербург",
    industry: "Косметика и FMCG",
  },
  year: "С 2013 года",
  services: [
    "Дизайн интернет-магазина",
    "Разработка мультибрендового каталога",
    "Карточка товара и корзина",
    "Бонусная программа и профиль покупателя",
    "Поддержка и развитие проекта",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
    { label: "Поддержка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "Levrana" },
    { label: "Отрасль", value: "Косметика и FMCG" },
    { label: "Тип проекта", value: "Мультибрендовая e-commerce платформа" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Мультибрендовая платформа с несколькими собственными брендами на одном сайте",
      "Каталог категорий косметики, товаров для дома, БАДов и pet-продукции",
      "Фильтры каталога и карточка товара с быстрым добавлением в корзину",
      "Бонусный счёт, раздел акций и избранное",
      "Корзина и личный профиль покупателя",
      "Постоянная поддержка и развитие проекта после запуска",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Российский бренд Levrana, основанный в 2013 году в Санкт-Петербурге, вырос из одного бренда косметики в несколько собственных линеек и продуктовых категорий — косметику, товары для дома, БАДы и pet-продукцию. Нужна была платформа, которая выдерживает такой ассортимент и остаётся понятной покупателю.",
        "Вторая часть задачи — не разовый запуск, а постоянная поддержка: платформа развивается вместе с ассортиментом и сценариями покупки.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: премиальная подача крупного бренда",
    paragraphs: [
      "Интерфейс собран на нейтральной премиальной подложке — уровень подачи соответствует крупному FMCG-бренду, а не небольшому магазину косметики.",
      "Каталог и карточка товара выстроены так, чтобы разные бренды и категории читались как единая система, а не набор разрозненных разделов.",
    ],
    bullets: [
      "Нейтральная премиальная подложка вместо чистого белого",
      "Единая система для нескольких брендов на одной платформе",
      "Быстрый add-to-cart прямо из каталога",
    ],
  },

  identity: {
    fonts: [
      { name: "Inter Tight", role: "Заголовки", sample: "Каталог Levrana" },
      { name: "Golos Text", role: "Интерфейс и описания", sample: "Бонусная программа" },
    ],
    colors: [
      { hex: "#F5F1EA", name: "Фон витрины" },
      { hex: "#8B6F4E", name: "Акцент бренда" },
      { hex: "#1E1C19", name: "Текст" },
    ],
  },

  cover: img(
    "levrana-case-cover",
    "Главная страница мультибрендовой e-commerce платформы Levrana",
    6,
  ),

  gallery: [
    img("levrana-brands-catalog", "Каталог брендов и категорий на платформе Levrana", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: выбор бренда или категории, карточка товара и корзина в один сценарий.",
      "Состояния корзины, избранного и профиля в мобильной версии показаны заполненными, как в реальном использовании.",
    ],
    images: [
      mobile("levrana-mobile-hero", "Мобильная главная e-commerce платформы Levrana"),
      mobile("levrana-mobile-loyalty", "Мобильный бонусный счёт и профиль покупателя Levrana"),
    ],
  },

  columnBlocks: [
    {
      id: "brands",
      title: "Мультибрендовая платформа",
      paragraphs: [
        "На одной платформе представлены несколько собственных брендов Levrana и разные продуктовые направления. Структура каталога позволяет покупателю двигаться и от бренда, и от категории — косметики, товаров для дома, БАДов или pet-продукции.",
      ],
      columns: [
        { title: "Бренды", items: ["Несколько собственных брендов", "Единая витрина"] },
        { title: "Категории", items: ["Косметика", "Товары для дома"] },
        { title: "Дополнительно", items: ["БАДы", "Pet-продукция"] },
      ],
    },
    {
      id: "catalog",
      title: "Большой каталог",
      paragraphs: [
        "Каталог с фильтрами и карточкой товара рассчитан на большой ассортимент: покупатель быстро сужает выбор и добавляет товар в корзину без перехода в карточку.",
      ],
      bullets: [
        "Фильтры по категориям и направлениям",
        "Карточка товара с ключевыми характеристиками",
        "Быстрый add-to-cart из каталога",
      ],
    },
    {
      id: "loyalty",
      title: "Лояльность и e-commerce",
      paragraphs: [
        "Покупателю доступны бонусный счёт, акции, избранное, корзина и личный профиль — стандартный набор e-commerce сценариев для повторных покупок.",
      ],
      bullets: [
        "Бонусный счёт и накопление баллов",
        "Раздел акций",
        "Избранное, корзина и профиль",
      ],
    },
    {
      id: "support",
      title: "Развитие после запуска",
      paragraphs: [
        "После запуска платформа остаётся на нашей поддержке: развиваем каталог и сценарии покупки по мере роста ассортимента бренда.",
      ],
      bullets: [
        "Постоянная техническая поддержка",
        "Развитие каталога вместе с ассортиментом",
      ],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка интернет-магазина", to: routes.ecom },
      { label: "Поддержка и развитие", to: routes.ecom },
    ],
  },
};
